'use client';

import { useState, useMemo, useCallback } from 'react';
import Link from 'next/link';
import { cn } from '@/core/utils/cn';
import { useLocalization } from '@/core/hooks/useLocalization';
import {
  Star, Users, BedDouble, Maximize, Check, Sparkles, Eye,
} from 'lucide-react';
import { useHotelRoomTypes } from '../hooks/useHotels';
import { ROOM_TYPE_FILTERS } from './hotel.constants';
import type { HotelRoomTypeData } from '../types/hotels.types';
import { useTranslation } from '@/core/i18n';

/* ─── Props ──────────────────────────────────────────────────────────── */

interface HotelRoomTypesProps {
  selectedType: string;
  onSelectType: (slug: string) => void;
  nights: number;
  hotelSlug: string;
  dateQuery: string;
}

/* ─── Star Rating ────────────────────────────────────────────────────── */

function MiniStarRating({ rating }: { rating: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={11}
          className={cn(
            i < Math.round(rating)
              ? 'fill-hms-gold text-hms-gold'
              : 'fill-transparent text-hms-textDim/40',
          )}
        />
      ))}
    </div>
  );
}

/* ─── Room Type Card ─────────────────────────────────────────────────── */

function RoomTypeCard({
  room, selected, nights, onSelect, formatMoney, detailUrl,
}: {
  room: HotelRoomTypeData;
  selected: boolean;
  nights: number;
  onSelect: () => void;
  formatMoney: (n: number) => string;
  detailUrl: string;
}) {
  const totalPrice = nights > 0 ? room.pricePerNight * nights : room.pricePerNight;
  // Guard the divisor explicitly: with originalPrice = 0 this yields NaN, and
  // while `discount > 0` happens to hide the badge (NaN > 0 is false), relying
  // on that is accidental. 0 means "no discount", which is the intended read.
  const discount = room.originalPrice > 0
    ? Math.round(((room.originalPrice - room.pricePerNight) / room.originalPrice) * 100)
    : 0;

  return (
    <div
      id={`hotels-detail-room-${room.slug}`}
      className={cn(
        'rounded-xl border-2 bg-hms-card overflow-hidden transition-all',
        selected
          ? 'border-hms-gold shadow-[0_0_24px_rgba(200,145,58,0.15)]'
          : 'border-hms-border hover:border-hms-borderLt',
      )}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Photo */}
        <div
          role="button"
          tabIndex={0}
          onClick={onSelect}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(); }}
          className="relative w-full sm:w-[220px] h-[180px] sm:h-auto shrink-0 overflow-hidden cursor-pointer"
        >
          <img
            src={room.photo}
            alt={room.name}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          {discount > 0 && (
            <div className="absolute top-3 left-3 bg-hms-red text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              {discount}% OFF
            </div>
          )}
          {selected && (
            <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-hms-gold flex items-center justify-center shadow-lg shadow-hms-gold/30">
              <Check className="w-4 h-4 text-black" strokeWidth={3} />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 p-5 flex flex-col">
          <div
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(); }}
            className="flex items-start justify-between gap-4 cursor-pointer"
          >
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-bold text-hms-text">{room.name}</h3>
              <p className="text-xs text-hms-textSub mt-1 line-clamp-2">{room.description}</p>
            </div>
            <div className="text-right shrink-0">
              <div className="flex items-baseline gap-1.5">
                <span className="text-lg font-extrabold text-hms-gold">{formatMoney(room.pricePerNight)}</span>
              </div>
              <span className="text-xs text-hms-textDim line-through">{formatMoney(room.originalPrice)}</span>
              <p className="text-[10px] text-hms-textDim">/ night</p>
            </div>
          </div>

          {/* Details row */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
            <span className="inline-flex items-center gap-1 text-[11px] text-hms-textSub">
              <Maximize size={12} className="text-hms-gold" /> {room.size}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-hms-textSub">
              <BedDouble size={12} className="text-hms-gold" /> {room.bedType}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-hms-textSub">
              <Users size={12} className="text-hms-gold" /> Up to {room.maxGuests} guests
            </span>
          </div>

          {/* Amenity pills */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {room.amenities.map((a) => (
              <span key={a} className="text-[10px] px-2 py-0.5 rounded-full border border-hms-border/60 bg-hms-surface text-hms-textSub">
                {a}
              </span>
            ))}
          </div>

          {/* Highlights */}
          {room.highlights.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3">
              {room.highlights.map((h) => (
                <span key={h} className="inline-flex items-center gap-1 text-[10px] font-medium text-hms-teal">
                  <Sparkles size={10} /> {h}
                </span>
              ))}
            </div>
          )}

          {/* Footer: rating + availability + action */}
          <div className="flex items-center justify-between mt-auto pt-4 border-t border-hms-border/40">
            <div className="flex items-center gap-3">
              {/* Rating */}
              <div className="flex items-center gap-1.5">
                <MiniStarRating rating={room.rating} />
                <span className="text-xs font-semibold text-hms-gold">{room.rating}</span>
                <span className="text-[10px] text-hms-textDim">({room.reviewCount})</span>
              </div>
              {/* Availability */}
              <span className="text-[10px] text-hms-textDim">
                {room.available} room{room.available !== 1 ? 's' : ''} left
              </span>
            </div>

            <div className="flex items-center gap-2">
              {nights > 0 && (
                <span className="text-xs font-bold text-hms-teal">
                  {formatMoney(totalPrice)} / {nights}N
                </span>
              )}
              <Link
                id={`hotels-detail-room-view-${room.slug}`}
                href={detailUrl}
                className="h-9 px-4 rounded-lg text-xs font-semibold border border-hms-border text-hms-textSub hover:border-hms-borderLt hover:text-hms-text transition-all inline-flex items-center gap-1.5"
              >
                <Eye size={13} /> Details
              </Link>
              <button
                id={`hotels-detail-room-select-${room.slug}`}
                type="button"
                onClick={onSelect}
                className={cn(
                  'h-9 px-5 rounded-lg text-xs font-bold transition-all',
                  selected
                    ? 'bg-hms-gold text-black cursor-default'
                    : 'bg-hms-gold/10 text-hms-gold border border-hms-gold/30 hover:bg-hms-gold hover:text-black',
                )}
              >
                {selected ? 'Selected' : 'Select Room'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────── */

export function HotelRoomTypes({ selectedType, onSelectType, nights, hotelSlug, dateQuery }: HotelRoomTypesProps) {
  const { formatMoney } = useLocalization();
  const { data: roomTypesData } = useHotelRoomTypes();
  const hotelRoomTypes = useMemo(() => ((roomTypesData as unknown as Record<string, unknown>)?.data ?? []) as HotelRoomTypeData[], [roomTypesData]);
  const [filter, setFilter] = useState<typeof ROOM_TYPE_FILTERS[number]>('All');

  const filtered = useMemo(() => {
    if (filter === 'All') return hotelRoomTypes;
    return hotelRoomTypes.filter((r) => r.filterGroup === filter);
  }, [filter, hotelRoomTypes]);

  const handleSelect = useCallback((slug: string) => {
    onSelectType(slug);
  }, [onSelectType]);

  return (
    <div id="hotels-detail-rooms-section" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 id="hotels-detail-rooms-title" className="text-lg font-semibold text-hms-text">Rooms &amp; Suites</h2>
        <span className="text-xs text-hms-textDim">{hotelRoomTypes.length} room types available</span>
      </div>

      {/* Filter tabs */}
      <div id="hotels-detail-room-filters" className="flex gap-2 flex-wrap">
        {ROOM_TYPE_FILTERS.map((f) => (
          <button
            key={f}
            id={`hotels-detail-room-filter-${f.toLowerCase()}`}
            type="button"
            onClick={() => setFilter(f)}
            className={cn(
              'h-8 px-4 rounded-lg text-xs font-medium transition-colors',
              f === filter
                ? 'bg-hms-gold text-black'
                : 'border border-hms-border text-hms-textSub hover:border-hms-gold/30',
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Room cards */}
      <div className="space-y-4">
        {filtered.map((room) => (
          <RoomTypeCard
            key={room.slug}
            room={room}
            selected={selectedType === room.slug}
            nights={nights}
            onSelect={() => handleSelect(room.slug)}
            formatMoney={formatMoney}
            detailUrl={`/hotels/${hotelSlug}/rooms/${room.slug}${dateQuery ? `?${dateQuery}` : ''}`}
          />
        ))}
      </div>
    </div>
  );
}
